import React from 'react'
import { EntityWizardShell } from '@klear/ui/wizard/entity-shell'
import {
  CustomerFlow,
  type CustomerFlowMode,
  type CustomerStepGroupResult
} from '@klear/customer-ui/customer-step-group'
import { useCredit } from '@klear/customer-ui/use-credit'
import { useCustomer } from '@klear/customer-ui/use-customers'

interface CustomerWizardProps {
  mode: CustomerFlowMode
  customerId?: number
  traderId?: number
  title?: string
  showTraderSelect?: boolean
  showIdentifiers?: boolean
  showParentOrganization?: boolean
  showCompanyLookup?: boolean
  canEdit?: boolean
  canViewCustomerCreditRatings?: boolean
  canEditCustomerCreditRatings?: boolean
  viewActions?: React.ReactNode
  onBack: () => void
  onDone: (result: CustomerStepGroupResult) => void
  onEdit?: () => void
}

export function CustomerWizard({
  mode,
  customerId,
  traderId,
  title,
  showTraderSelect = false,
  showIdentifiers = false,
  showParentOrganization = false,
  showCompanyLookup = true,
  canEdit = false,
  canViewCustomerCreditRatings = false,
  canEditCustomerCreditRatings = false,
  viewActions,
  onBack,
  onDone,
  onEdit
}: CustomerWizardProps) {
  const {
    customer,
    error,
    isLoading: isLoadingCustomer
  } = useCustomer(customerId ?? 0, { enabled: mode !== 'create', traderId })

  const { credit, isLoading: isLoadingCredit } = useCredit(customer?.organizationId, {
    enabled: canViewCustomerCreditRatings && mode !== 'create' && customer?.organizationId != null
  })

  return (
    <EntityWizardShell
      mode={mode}
      entityLabel="Customer"
      entityName={customer?.name}
      title={title}
      canEdit={canEdit}
      viewActions={viewActions}
      isLoading={isLoadingCustomer || isLoadingCredit}
      error={error}
      notFound={mode !== 'create' && !customer}
      notFoundLabel="Customer not found"
      onBack={onBack}
      onEdit={onEdit}
    >
      {({ activeMode, onNavStateChange, cancelEdit, requestExit, onDirtyChange }) => (
        <CustomerFlow
          key={`${activeMode}:${customerId ?? 'new'}:${traderId ?? ''}:${credit?.organizationId ?? ''}`}
          mode={activeMode}
          customer={customer ?? undefined}
          credit={credit}
          customerId={customerId}
          traderId={traderId}
          showTraderSelect={showTraderSelect}
          showIdentifiers={showIdentifiers}
          showParentOrganization={showParentOrganization}
          showCompanyLookup={showCompanyLookup}
          canViewCustomerCreditRatings={canViewCustomerCreditRatings}
          canEditCustomerCreditRatings={canEditCustomerCreditRatings}
          onNavStateChange={onNavStateChange}
          onDirtyChange={onDirtyChange}
          onComplete={onDone}
          onCancel={activeMode === 'create' ? requestExit : cancelEdit}
        />
      )}
    </EntityWizardShell>
  )
}
